import React, { useState, useMemo } from "react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import fr from "date-fns/locale/fr";
import "moment/locale/fr";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { Button } from "../../../_helpers/components/Button";

const COLORS = [
    "#60a5fa",
    "#34d399",
    "#facc15",
    "#f87171",
    "#a78bfa",
    "#fb923c",
];

const CustomToolbar = ({ label, onNavigate }) => {
    return (
        <div className="flex justify-between items-center py-2 bg-white border-b">
            <div className="flex items-center space-x-2">
                <Button
                    action={() => {
                        onNavigate("TODAY");
                    }}
                    name={"Aujourd'hui"}
                    type={"empty"}
                />
                <Button
                    action={() => {
                        onNavigate("PREV");
                    }}
                    name={"Précédent"}
                    type={"empty"}
                />
                <Button
                    action={() => {
                        onNavigate("NEXT");
                    }}
                    name={"Suivant"}
                    type={"empty"}
                />
            </div>
            <div className="text-lg font-semibold">{label}</div>
        </div>
    );
};

export const Calendrier = () => {
    moment.locale("fr");
    const locales = {
        fr: fr,
    };
    const localizer = dateFnsLocalizer({
        format,
        parse,
        startOfWeek,
        getDay,
        locales,
    });
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleSelectEvent = (event) => {
        setSelectedEvent({
            start: moment(event.start).format("YYYY-MM-DDTHH:mm"),
            end: moment(event.end).format("YYYY-MM-DDTHH:mm"),
        });
    };

    const handleSelectSlot = (slotInfo) => {
        setSelectedSlot({
            start: moment(slotInfo.start).format("YYYY-MM-DDTHH:mm"),
            end: moment(slotInfo.end).format("YYYY-MM-DDTHH:mm"),
        });
        setSidebarOpen(true); // on ouvre la sidebar
    };

    const activities = [
        {
            title: "Atelier peinture",
            start: "2025-05-28T10:00:00",
            end: "2025-05-28T11:30:00",
            lieu: "Salle des fêtes",
            intervenant: "Claire Dupont",
            projet: "Projet artistique",
            statut: "vendu",
            regroupement: "Groupe A",
            langue: "fr",
            identifiant: "BIL-001",
            placesReservees: 5,
            placesVendues: 10,
        },
        {
            title: "Conférence patrimoine",
            start: "2025-05-30T14:00:00",
            end: "2025-05-30T15:30:00",
            lieu: "Médiathèque",
            intervenant: "Jean Martin",
            projet: "Histoire locale",
            statut: "réservé",
            regroupement: "Groupe B",
            langue: "fr",
            identifiant: "BIL-002",
            placesReservees: 3,
            placesVendues: 12,
        },
        // ... autres activités ...
    ];
    const participationActivities = [
        {
            start: "2025-06-01T10:00:00",
            end: "2025-06-01T11:30:00",
            title: "Atelier peinture",
            lieu: "Salle polyvalente",
            participants: [
                { name: "Alice Dupont", status: "Oui" },
                { name: "Jean Martin", status: "Indécis" },
                { name: "Claire Robert", status: "Non" },
            ],
        },
        // ...
    ];

    const [view, setView] = useState("week");
    const [filters, setFilters] = useState({
        lieu: "",
        statut: "",
        regroupement: "",
    });
    const [activeTab, setActiveTab] = useState("activites"); // "info" | "participants" | "historique"
    const [newTitle, setNewTitle] = useState("");
    const handleCreateActivity = (e) => {
        e.preventDefault();
        const newEvent = {
            title: newTitle,
            start: selectedSlot.start,
            end: selectedSlot.end,
        };
        setEvents([...events, newEvent]); // ou appel API
        setSidebarOpen(false);
        setSelectedSlot(null);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const filteredActivities = useMemo(() => {
        return activities.filter((a) => {
            return (
                (filters.lieu === "" || a.lieu === filters.lieu) &&
                (filters.statut === "" || a.statut === filters.statut) &&
                (filters.regroupement === "" ||
                    a.regroupement === filters.regroupement)
            );
        });
    }, [filters, activities]);

    const upcomingActivities = useMemo(() => {
        const now = new Date();
        return filteredActivities
            .filter((act) => new Date(act.start) > now)
            .sort((a, b) => new Date(a.start) - new Date(b.start))
            .slice(0, 10);
    }, [filteredActivities]);

    const events = useMemo(() => {
        return filteredActivities.map((activity, index) => ({
            ...activity,
            title: activity.title || "Activité",
            start: new Date(activity.start),
            end: new Date(activity.end),
            color: COLORS[index % COLORS.length],
        }));
    }, [filteredActivities]);

    const eventStyleGetter = (event) => {
        const backgroundColor = event.color || "#60a5fa";
        return {
            style: {
                backgroundColor,
                borderRadius: "6px",
                color: "white",
                border: "none",
                display: "block",
                padding: "4px",
            },
        };
    };

    const getDateRange = () => {
        if (view === "next3days") {
            const start = new Date();
            const end = new Date();
            end.setDate(start.getDate() + 3);
            return { start, end };
        }
        return {};
    };

    const { start, end } = getDateRange();

    const messagesFr = {
        allDay: "Toute la journée",
        previous: "Précédent",
        next: "Suivant",
        today: "Aujourd'hui",
        date: "Date",
        time: "Heure",
        event: "Événement",
        noEventsInRange: "Aucun événement dans cette période.",
        showMore: (total) => `+ ${total} autres`,
    };

    // Pour les menus déroulants
    const lieux = [...new Set(activities.map((a) => a.lieu))];
    const statuts = [...new Set(activities.map((a) => a.statut))];
    const regroupements = [...new Set(activities.map((a) => a.regroupement))];

    return (
        <div className="p-6 pt-0 space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-4"></div>

            <div className="flex gap-4 flex-wrap justify-between">
                <div className="flex flex-wrap gap-3">
                    {/* Lieu */}
                    <select
                        name="lieu"
                        value={filters.lieu}
                        onChange={handleFilterChange}
                        className="font-bold border-2 border-app-primary text-app-primary hover:border-app-secondary focus:border-app-secondary rounded-lg px-4 py-2 transition bg-white"
                    >
                        <option value="">Tous les lieux</option>
                        {lieux.map((lieu, i) => (
                            <option key={i} value={lieu}>
                                {lieu}
                            </option>
                        ))}
                    </select>

                    {/* Statut */}
                    <select
                        name="statut"
                        value={filters.statut}
                        onChange={handleFilterChange}
                        className="font-bold border-2 border-app-primary text-app-primary hover:border-app-secondary focus:border-app-secondary rounded-lg px-4 py-2 transition bg-white"
                    >
                        <option value="">Tous les statuts</option>
                        {statuts.map((s, i) => (
                            <option key={i} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>

                    {/* Regroupement */}
                    <select
                        name="regroupement"
                        value={filters.regroupement}
                        onChange={handleFilterChange}
                        className="font-bold border-2 border-app-primary text-app-primary hover:border-app-secondary focus:border-app-secondary rounded-lg px-4 py-2 transition bg-white"
                    >
                        <option value="">Tous les regroupements</option>
                        {regroupements.map((r, i) => (
                            <option key={i} value={r}>
                                {r}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <div className="space-x-2">
                        <Button
                            action={() => {
                                setView("month");
                            }}
                            name={
                                <span className="flex items-center gap-1">
                                    <svg
                                        className="shrink-0 mr-2 w-6 h-6"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                    Mois
                                </span>
                            }
                            type={"empty"}
                        />
                        <Button
                            action={() => {
                                setView("week");
                            }}
                            name={
                                <span className="flex items-center gap-1">
                                    <svg
                                        className="shrink-0 mr-2 w-6 h-6"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                    Semaine
                                </span>
                            }
                            type={"empty"}
                        />
                        <Button
                            action={() => {
                                setView("next3days");
                            }}
                            name={
                                <span className="flex items-center gap-1">
                                    <svg
                                        className="shrink-0 mr-2 w-6 h-6"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                    Quotidien
                                </span>
                            }
                            type={"empty"}
                        />

                        <Button
                            action={() => {
                                setView("tableau");
                            }}
                            name={
                                <span className="flex items-center gap-1">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="shrink-0 mr-2 w-6 h-6"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <rect
                                            x="3"
                                            y="3"
                                            width="18"
                                            height="18"
                                            rx="2"
                                            ry="2"
                                            strokeWidth="2"
                                        />
                                        <line
                                            x1="3"
                                            y1="9"
                                            x2="21"
                                            y2="9"
                                            strokeWidth="2"
                                        />
                                        <line
                                            x1="3"
                                            y1="15"
                                            x2="21"
                                            y2="15"
                                            strokeWidth="2"
                                        />
                                        <line
                                            x1="9"
                                            y1="3"
                                            x2="9"
                                            y2="21"
                                            strokeWidth="2"
                                        />
                                        <line
                                            x1="15"
                                            y1="3"
                                            x2="15"
                                            y2="21"
                                            strokeWidth="2"
                                        />
                                    </svg>
                                    Tableau
                                </span>
                            }
                            type={"empty"}
                        />
                        <button
                            onClick={() => navigate("")}
                            className="p-2 rounded-full hover:bg-green-100 transition"
                            title="Exporter"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 text-green-700"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <div style={{ height: "600px" }}>
                {view === "tableau" ? (
                    <>
                        <div className="text-sm font-medium text-center text-gray-500 border-b border-gray-200">
                            <ul className="p-0 flex flex-wrap -mb-px">
                                <li className="me-2">
                                    <button
                                        onClick={() =>
                                            setActiveTab("activites")
                                        }
                                        className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === "activites"
                                                ? "text-blue-600 border-blue-600"
                                                : "border-transparent hover:text-gray-600 hover:border-gray-300"
                                            }`}
                                    >
                                        Activités
                                    </button>
                                </li>
                                <li className="me-2">
                                    <button
                                        onClick={() =>
                                            setActiveTab("participations")
                                        }
                                        className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === "participations"
                                                ? "text-blue-600 border-blue-600"
                                                : "border-transparent hover:text-gray-600 hover:border-gray-300"
                                            }`}
                                    >
                                        Participations
                                    </button>
                                </li>
                            </ul>
                        </div>
                        {activeTab === "activites" && (
                            <div>
                                <table className="min-w-full table-auto border rounded-sm overflow-hidden shadow-sm text-sm">
                                    <thead className="bg-gray-100 text-gray-700">
                                        <tr>
                                            <th className="px-4 py-2 text-left">
                                                Date
                                            </th>
                                            <th className="px-4 py-2 text-left">
                                                Titre
                                            </th>
                                            <th className="px-4 py-2 text-left">
                                                Lieu
                                            </th>
                                            <th className="px-4 py-2 text-left">
                                                Durée
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {upcomingActivities.map((act, i) => (
                                            <tr
                                                key={i}
                                                className="border-t hover:bg-gray-50 transition"
                                            >
                                                <td className="px-4 py-2">
                                                    {moment(act.start).format(
                                                        "DD/MM/YYYY HH:mm"
                                                    )}
                                                </td>
                                                <td className="px-4 py-2">
                                                    {act.title}
                                                </td>
                                                <td className="px-4 py-2">
                                                    {act.lieu || "—"}
                                                </td>
                                                <td className="px-4 py-2">
                                                    {moment(act.end).diff(
                                                        moment(act.start),
                                                        "minutes"
                                                    )}{" "}
                                                    min
                                                </td>
                                            </tr>
                                        ))}
                                        {upcomingActivities.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan="4"
                                                    className="text-center text-gray-500 p-4"
                                                >
                                                    Aucune activité à venir.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {activeTab === "participations" && (
                            <div className="mt-8">
                                <div className="mt-8">
                                    <table className="min-w-full table-auto border rounded-sm overflow-hidden shadow-sm text-sm">
                                        <thead className="bg-gray-100 text-gray-700">
                                            <tr>
                                                <th className="px-4 py-2 text-left">
                                                    Date
                                                </th>
                                                <th className="px-4 py-2 text-left">
                                                    Titre
                                                </th>
                                                <th className="px-4 py-2 text-left">
                                                    Lieu
                                                </th>
                                                <th className="px-4 py-2 text-left">
                                                    Durée
                                                </th>
                                                <th className="px-4 py-2 text-left">
                                                    Participant
                                                </th>
                                                <th className="px-4 py-2 text-left">
                                                    Statut
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {participationActivities.flatMap(
                                                (activity, i) =>
                                                    activity.participants.map(
                                                        (participant, j) => (
                                                            <tr
                                                                key={`${i}-${j}`}
                                                                className="border-t hover:bg-gray-50 transition"
                                                            >
                                                                <td className="px-4 py-2">
                                                                    {moment(
                                                                        activity.start
                                                                    ).format(
                                                                        "DD/MM/YYYY HH:mm"
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-2">
                                                                    {
                                                                        activity.title
                                                                    }
                                                                </td>
                                                                <td className="px-4 py-2">
                                                                    {activity.lieu ||
                                                                        "—"}
                                                                </td>
                                                                <td className="px-4 py-2">
                                                                    {moment(
                                                                        activity.end
                                                                    ).diff(
                                                                        moment(
                                                                            activity.start
                                                                        ),
                                                                        "minutes"
                                                                    )}{" "}
                                                                    min
                                                                </td>
                                                                <td className="px-4 py-2">
                                                                    {
                                                                        participant.name
                                                                    }
                                                                </td>
                                                                <td className="px-4 py-2">
                                                                    <span
                                                                        className={
                                                                            participant.status ===
                                                                                "Oui"
                                                                                ? "text-green-600"
                                                                                : participant.status ===
                                                                                    "Non"
                                                                                    ? "text-red-600"
                                                                                    : "text-yellow-600"
                                                                        }
                                                                    >
                                                                        {
                                                                            participant.status
                                                                        }
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )
                                                    )
                                            )}
                                            {participationActivities.length ===
                                                0 && (
                                                    <tr>
                                                        <td
                                                            colSpan="6"
                                                            className="text-center text-gray-500 p-4"
                                                        >
                                                            Aucune participation
                                                            enregistrée.
                                                        </td>
                                                    </tr>
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <Calendar
                        localizer={localizer}
                        culture="fr"
                        onSelectSlot={handleSelectSlot} // ← gestion du clic
                        selectable={true} // ← à ne pas oublier !
                        onSelectEvent={handleSelectEvent} // ← ICI
                        tooltipAccessor={(event) => {
                            return (
                                `🎨 ${event.title || "Activité"}\n` +
                                `📅 Le ${moment(event.start).format(
                                    "dddd D MMMM YYYY"
                                )}\n` +
                                `🕗 De ${moment(event.start).format(
                                    "HH:mm"
                                )} à ${moment(event.end).format("HH:mm")}\n` +
                                `📍 Lieu : ${event.lieu || "Non spécifié"}\n` +
                                `👤 Intervenant : ${event.intervenant || "Non précisé"
                                }\n` +
                                `🧩 Projet : ${event.projet || "N/A"}\n` +
                                `🎟️ Statut : ${event.statut || "N/A"}\n` +
                                `👥 Regroupement : ${event.regroupement || "N/A"
                                }\n` +
                                `🗣️ Langue : ${event.langue || "N/A"}\n` +
                                `🆔 Identifiant : ${event.identifiant || "N/A"
                                }\n` +
                                `📌 Places réservées : ${event.placesReservees ?? "0"
                                }\n` +
                                `✅ Places vendues : ${event.placesVendues ?? "0"
                                }`
                            );
                        }}
                        step={15} // ✅ pas de 15 minutes
                        timeslots={1}
                        events={
                            view === "next3days"
                                ? events.filter(
                                    (e) => e.start >= start && e.start <= end
                                )
                                : events
                        }
                        min={new Date(1970, 1, 1, 7, 0, 0)} // ← ICI : début à 7h
                        max={new Date(1970, 1, 1, 20, 0, 0)} // ← ICI : Max à 20h
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: "100%" }}
                        view={
                            view === "next3days"
                                ? Views.DAY
                                : view === "week"
                                    ? Views.WEEK
                                    : Views.MONTH
                        }
                        views={{
                            month: true,
                            week: true,
                            day: true,
                        }}
                        components={{
                            toolbar: CustomToolbar, // <-- personnalise la toolbar ici
                        }}
                        eventPropGetter={eventStyleGetter}
                        messages={messagesFr}
                    />
                )}
            </div>
            {sidebarOpen && (
                <div className="fixed top-0 right-0 w-full md:w-96 h-full bg-white shadow-lg z-50 p-4 overflow-y-auto">
                    <h2 className="text-xl font-bold mb-4">
                        Créer une activité
                    </h2>
                    <form onSubmit={handleCreateActivity}>
                        <label className="block mb-2">Titre</label>
                        <input
                            type="text"
                            className="w-full border rounded-sm p-2 mb-4"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                        />

                        <label className="block mb-2">Début</label>
                        <input
                            type="datetime-local"
                            className="w-full border rounded-sm p-2 mb-4"
                            value={
                                selectedSlot?.start
                                    ? moment(selectedSlot.start).format(
                                        "YYYY-MM-DDTHH:mm"
                                    )
                                    : ""
                            }
                            onChange={(e) =>
                                setSelectedSlot((prev) => ({
                                    ...prev,
                                    start: new Date(e.target.value),
                                }))
                            }
                        />

                        <label className="block mb-2">Fin</label>
                        <input
                            type="datetime-local"
                            className="w-full border rounded-sm p-2 mb-4"
                            value={
                                selectedSlot?.end
                                    ? moment(selectedSlot.end).format(
                                        "YYYY-MM-DDTHH:mm"
                                    )
                                    : ""
                            }
                            onChange={(e) =>
                                setSelectedSlot((prev) => ({
                                    ...prev,
                                    end: new Date(e.target.value),
                                }))
                            }
                        />

                        <button
                            type="submit"
                            className="bg-blue-600 text-white px-4 py-2 rounded-sm"
                        >
                            Enregistrer
                        </button>
                    </form>

                    <button
                        className="absolute top-4 right-4 text-gray-500"
                        onClick={() => setSidebarOpen(false)}
                    >
                        ✖
                    </button>
                </div>
            )}
        </div>
    );
};
